/**
 * 5.56 Auto-Restock Redirect Page
 * Detects the one-time 300/500/1000-round AOV kit in cart and swaps it to the
 * matching subscription bundle with cadence-matched shipping insurance.
 */
(function () {
  'use strict';

  if (customElements.get('auto-restock-redirect')) return;

  var OFFER_ID = '556-tiered-gift';

  var COPY = {
    300: {
      cartTitle: 'Your 300-round range kit is ready.',
      summary: '300 rounds of PMC X-TAC 5.56, 200 rounds of Winchester 9mm, and 20 splatter targets.',
      bundleLabel: '500-round range bundle',
      mainItem: '300 rounds of PMC X-TAC 5.56 NATO',
      bonusItem: '200 rounds of Winchester 9mm 115gr + 20 splatter targets',
      bonusItems: ['200 rounds Winchester 9mm', '20 splatter targets'],
      bonusValueCents: 8000,
      cadence: '30-day delivery',
      delivery: 'No remembering to reorder, no re-shopping ammo, no hoping the promo is still running later.'
    },
    500: {
      cartTitle: 'Your 500-round training kit is ready.',
      summary: '500 rounds of PMC X-TAC 5.56, 400 rounds of Winchester 9mm, and 40 splatter targets.',
      bundleLabel: '900-round training bundle',
      mainItem: '500 rounds of PMC X-TAC 5.56 NATO',
      bonusItem: '400 rounds of Winchester 9mm 115gr + 40 splatter targets',
      bonusItems: ['400 rounds Winchester 9mm', '40 splatter targets'],
      bonusValueCents: 16000,
      cadence: '60-day delivery',
      delivery: 'No remembering to reorder, no re-shopping ammo, no hoping the promo is still running later.'
    },
    1000: {
      cartTitle: 'Your 1,000-round stockpile is ready.',
      summary: '1,000 rounds of PMC X-TAC 5.56, 800 rounds of Winchester 9mm, 80 splatter targets, and a We The People ammo can.',
      bundleLabel: '1,800-round stockpile bundle',
      mainItem: '1,000 rounds of PMC X-TAC 5.56 NATO',
      bonusItem: '800 rounds of Winchester 9mm 115gr + 80 splatter targets + engraved .50 cal ammo can',
      bonusItems: ['800 rounds Winchester 9mm', '80 splatter targets', 'Engraved .50 cal ammo can'],
      bonusValueCents: 38500,
      cadence: '90-day delivery',
      delivery: 'No remembering to reorder, no re-shopping ammo, no hoping the promo is still running later.'
    }
  };

  customElements.define('auto-restock-redirect', class AutoRestockRedirect extends HTMLElement {
    constructor() {
      super();

      this.acceptButton = this.querySelector('[data-accept]');
      this.declineButton = this.querySelector('[data-decline]');
      this.errorEl = this.querySelector('[data-error]');
      this.detectedTier = null;
      this.hasMatchingCart = false;
      this.cartHadInsurance = false;
      this.tiers = {};
    }

    connectedCallback() {
      this.tiers = this.readTierConfig();

      if (this.acceptButton) {
        this.acceptButton.addEventListener('click', this.handleAccept.bind(this));
      }

      if (this.declineButton) {
        this.declineButton.addEventListener('click', this.handleDecline.bind(this));
      }

      this.bindFooterAccordions();
      this.initializeFromCart();
    }

    readTierConfig() {
      var tiers = {};
      ['300', '500', '1000'].forEach(function (tierKey) {
        tiers[tierKey] = {
          tier: tierKey,
          mainVariantId: this.toId(this.readTierAttribute('main', tierKey, 'variant-id')),
          giftVariantId: this.toId(this.readTierAttribute('gift', tierKey, 'variant-id')),
          restockVariantId: this.toId(this.readTierAttribute('restock', tierKey, 'variant-id')),
          sellingPlanId: this.toId(this.readTierAttribute('restock', tierKey, 'selling-plan-id')),
          frequency: this.toNumber(this.readTierAttribute('restock', tierKey, 'frequency')),
          priceCents: this.toNumber(this.readTierAttribute('restock', tierKey, 'price-cents')) || this.defaultPriceCents(tierKey)
        };
      }, this);
      return tiers;
    }

    readTierAttribute(prefix, tierKey, suffix) {
      return this.getAttribute('data-' + prefix + '-' + tierKey + '-' + suffix);
    }

    defaultPriceCents(tierKey) {
      if (tierKey === '1000') return 100000;
      if (tierKey === '500') return 50000;
      return 30000;
    }

    bindFooterAccordions() {
      var accordions = this.querySelectorAll('.ar556-footer__accordion');
      var sync = function () {
        accordions.forEach(function (details) {
          if (window.innerWidth >= 750) {
            details.setAttribute('open', '');
          }
        });
      };

      sync();
      window.addEventListener('resize', sync);
    }

    async initializeFromCart() {
      try {
        var cart = await this.fetchCart();
        this.cartHadInsurance = cart.items.some(this.isInsuranceLine.bind(this));
        this.detectedTier = this.detectTier(cart);
        this.hasMatchingCart = Boolean(this.detectedTier);
        this.detectedTier = this.detectedTier || '300';
        this.updateCopy(this.detectedTier);

        if (!this.hasMatchingCart) {
          this.classList.add('is-missing-cart');
          if (this.acceptButton) this.acceptButton.disabled = true;
          this.showError('We could not find the 5.56 kit in your cart. You can still continue to cart, or go back and add the kit again.');
        }
      } catch (error) {
        this.detectedTier = '300';
        this.updateCopy(this.detectedTier);
        this.showError('We could not read your cart yet. Refresh the page or continue to cart.');
      } finally {
        this.finishInitializing();
      }
    }

    finishInitializing() {
      this.classList.remove('is-initializing');
      this.setAttribute('aria-busy', 'false');
    }

    detectTier(cart) {
      var priorities = ['1000', '500', '300'];

      for (var i = 0; i < priorities.length; i++) {
        var giftTier = this.findTierFromGiftProperty(cart, priorities[i]);
        if (giftTier) return giftTier;
      }

      for (var j = 0; j < priorities.length; j++) {
        var tierKey = priorities[j];
        var tier = this.tiers[tierKey];
        var matched = cart.items.some(function (item) {
          return this.sameId(item.variant_id, tier.mainVariantId) ||
            this.sameId(item.variant_id, tier.giftVariantId) ||
            this.sameId(item.variant_id, tier.restockVariantId);
        }, this);

        if (matched) return tierKey;
      }

      for (var k = 0; k < priorities.length; k++) {
        var inferredTier = this.findTierFromTitle(cart, priorities[k]);
        if (inferredTier) return inferredTier;
      }

      return null;
    }

    findTierFromGiftProperty(cart, tierKey) {
      var found = cart.items.some(function (item) {
        var properties = item.properties || {};
        return properties._aov_offer === OFFER_ID && String(properties._aov_gift_tier) === tierKey;
      });

      return found ? tierKey : null;
    }

    findTierFromTitle(cart, tierKey) {
      var phrase = tierKey === '1000' ? '1000' : tierKey;
      var commaPhrase = tierKey === '1000' ? '1,000' : tierKey;

      var found = cart.items.some(function (item) {
        var text = [
          item.product_title,
          item.variant_title,
          item.title,
          item.sku
        ].join(' ').toLowerCase();

        return text.indexOf('5.56') !== -1 &&
          (text.indexOf(phrase) !== -1 || text.indexOf(commaPhrase) !== -1);
      });

      return found ? tierKey : null;
    }

    updateCopy(tierKey) {
      var copy = COPY[tierKey] || COPY[300];
      var tier = this.tiers[tierKey] || this.tiers[300];
      var discount = this.toNumber(this.dataset.discountPercent) || 5;
      var priceCents = tier.priceCents || this.defaultPriceCents(tierKey);
      var restockPrice = Math.round(priceCents * (100 - discount) / 100);
      var periodText = 'Every ' + tier.frequency + ' days';
      var periodLower = periodText.toLowerCase();
      var bonusValueText = this.formatMoneyShort(copy.bonusValueCents);

      this.setText('[data-copy-headline]', 'Keep the bonuses coming ' + periodLower + '.');
      this.setText('[data-copy-subhead]', 'Lock it in now: same ' + bonusValueText + ' bonus bundle ' + periodLower + ', ' + discount + '% off each shipment, and no hoping this promo is still around later.');
      this.setText('[data-copy-deal-title]', 'Keep This ' + bonusValueText + ' Bonus Bundle On Every Restock.');
      this.setText('[data-copy-deal-subhead]', 'You already unlocked it today. Lock it in now and we will send the same ammo + bonus stack ' + periodLower + ', with ' + discount + '% off and no re-shopping later.');
      this.setText('[data-copy-period]', periodText);
      this.setText('[data-copy-main-item]', copy.mainItem);
      this.setListItems('[data-copy-bonus-list]', copy.bonusItems || [copy.bonusItem]);
      this.setText('[data-copy-free-value]', this.formatMoney(copy.bonusValueCents));
      this.setText('[data-copy-delivery]', copy.delivery);
      this.setText('[data-copy-cta]', 'Yes, Lock In My Bonuses');
      this.setText('[data-copy-discount]', discount + '%');
      this.setText('[data-price-restock]', this.formatMoney(restockPrice));
      this.setText('[data-price-savings]', discount + '% Off');

      this.dataset.detectedTier = tierKey;
    }

    async handleAccept(event) {
      if (event) event.preventDefault();
      if (this.classList.contains('is-loading')) return;

      var tierKey = this.detectedTier || '300';
      var tier = this.tiers[tierKey];

      if (!tier || !tier.restockVariantId || !tier.sellingPlanId) {
        this.showError('This auto-restock option is not configured yet. Please continue to cart.');
        return;
      }

      this.safeTrack('aov556_restock_button_clicked', this.buildTrackingPayload(tierKey, {
        button_action: 'accept_subscription',
        button_text: this.getButtonText(this.acceptButton),
        button_location: 'auto_restock_offer'
      }));

      this.setLoading(true);
      this.hideError();

      try {
        var cart = await this.fetchCart();
        var detectedTier = this.detectTier(cart);
        if (!detectedTier) {
          throw new Error('We could not find the 5.56 kit in your cart. Please continue to cart or add the kit again.');
        }

        tierKey = detectedTier;
        tier = this.tiers[tierKey];
        this.detectedTier = tierKey;
        this.hasMatchingCart = true;
        this.classList.remove('is-missing-cart');
        this.updateCopy(tierKey);

        this.cartHadInsurance = cart.items.some(this.isInsuranceLine.bind(this));

        var items = [{
          id: tier.restockVariantId,
          quantity: 1,
          selling_plan: tier.sellingPlanId,
          properties: {
            _auto_restock_redirect: '556-aov',
            _original_aov_tier: tierKey
          }
        }];

        var insuranceItem = this.cartHadInsurance ? this.buildInsuranceItem(tier.frequency) : null;
        if (insuranceItem) {
          items.push(insuranceItem);
        }

        await this.addItems(items);
        await this.removeOriginalLines(cart);

        this.safeTrack('auto_restock_redirect_accepted', {
          tier: tierKey,
          variant_id: tier.restockVariantId,
          selling_plan_id: tier.sellingPlanId,
          frequency: tier.frequency,
          bonus_value: this.getBonusValueDollars(tierKey),
          restock_price: this.getRestockPriceDollars(tierKey),
          discount_percent: this.getDiscountPercent(),
          insurance_swapped: Boolean(insuranceItem)
        });

        this.redirectTo(this.dataset.checkoutUrl || '/checkout');
      } catch (error) {
        this.showError(error.message || 'Something went wrong. Please try again.');
        this.setLoading(false);
      }
    }

    handleDecline(event) {
      if (event) event.preventDefault();
      this.safeTrack('aov556_restock_button_clicked', this.buildTrackingPayload(this.detectedTier || 'unknown', {
        button_action: 'keep_one_time',
        button_text: this.getButtonText(this.declineButton),
        button_location: 'auto_restock_offer'
      }));
      this.safeTrack('auto_restock_redirect_declined', {
        tier: this.detectedTier || 'unknown',
        bonus_value: this.getBonusValueDollars(this.detectedTier),
        restock_price: this.getRestockPriceDollars(this.detectedTier),
        discount_percent: this.getDiscountPercent()
      });
      this.redirectTo(this.dataset.declineUrl || '/checkout');
    }

    async removeOriginalLines(cart) {
      var updates = {};

      cart.items.forEach(function (item) {
        if (this.shouldRemoveLine(item)) {
          updates[item.key] = 0;
        }
      }, this);

      if (!Object.keys(updates).length) return;

      await this.postJson('cart/update.js', { updates: updates });
    }

    shouldRemoveLine(item) {
      return this.isOriginalMainLine(item) ||
        this.isOriginalGiftLine(item) ||
        this.isRestockLine(item) ||
        this.isInsuranceLine(item);
    }

    isOriginalMainLine(item) {
      return this.sameId(item.variant_id, this.tiers[300].mainVariantId) ||
        this.sameId(item.variant_id, this.tiers[500].mainVariantId) ||
        this.sameId(item.variant_id, this.tiers[1000].mainVariantId);
    }

    isOriginalGiftLine(item) {
      var properties = item.properties || {};
      var knownGift = this.sameId(item.variant_id, this.tiers[300].giftVariantId) ||
        this.sameId(item.variant_id, this.tiers[500].giftVariantId) ||
        this.sameId(item.variant_id, this.tiers[1000].giftVariantId);

      return knownGift || properties._aov_offer === OFFER_ID;
    }

    isRestockLine(item) {
      return this.sameId(item.variant_id, this.tiers[300].restockVariantId) ||
        this.sameId(item.variant_id, this.tiers[500].restockVariantId) ||
        this.sameId(item.variant_id, this.tiers[1000].restockVariantId);
    }

    isInsuranceLine(item) {
      var insuranceVariantId = this.getInsuranceVariantId();
      if (insuranceVariantId && this.sameId(item.variant_id, insuranceVariantId)) return true;

      var text = [item.product_title, item.title, item.handle].join(' ').toLowerCase();
      return text.indexOf('shipping insurance') !== -1;
    }

    buildInsuranceItem(frequency) {
      if (window.SubscriptionInsuranceCore && typeof window.SubscriptionInsuranceCore.buildInsuranceItem === 'function') {
        return window.SubscriptionInsuranceCore.buildInsuranceItem('subscription', frequency);
      }

      var config = window.theme && window.theme.shopSettings;
      if (!config || !config.shippingInsuranceEnabled || !config.shippingInsuranceVariantId) return null;

      var item = {
        id: this.toId(config.shippingInsuranceVariantId),
        quantity: 1
      };
      var planId = config.insuranceSellingPlans && config.insuranceSellingPlans[frequency];
      if (planId) item.selling_plan = this.toId(planId);

      return item.id ? item : null;
    }

    getInsuranceVariantId() {
      return this.toId(window.theme && window.theme.shopSettings && window.theme.shopSettings.shippingInsuranceVariantId);
    }

    async fetchCart() {
      var response = await this.fetch()(this.endpoint('cart.js'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) throw new Error('Could not read cart');
      return response.json();
    }

    async addItems(items) {
      await this.postJson('cart/add.js', { items: items });
    }

    async postJson(path, body) {
      var response = await this.fetch()(this.endpoint(path), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });

      var payload = await response.json().catch(function () {
        return {};
      });

      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Cart update failed');
      }

      return payload;
    }

    endpoint(path) {
      var root = this.dataset.rootUrl ||
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
        '/';

      if (root.charAt(root.length - 1) !== '/') root += '/';
      return root + path;
    }

    fetch() {
      return window.__nativeFetch || window.fetch.bind(window);
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      [this.acceptButton, this.declineButton].forEach(function (button) {
        if (button) button.disabled = isLoading;
      });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    setText(selector, text) {
      this.querySelectorAll(selector).forEach(function (node) {
        node.textContent = text;
      });
    }

    setListItems(selector, items) {
      this.querySelectorAll(selector).forEach(function (list) {
        list.textContent = '';
        items.forEach(function (item) {
          var listItem = document.createElement('li');
          listItem.textContent = item;
          list.appendChild(listItem);
        });
      });
    }

    formatMoney(cents) {
      var amount = this.toNumber(cents) || 0;
      return '$' + (amount / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    formatMoneyShort(cents) {
      return this.formatMoney(cents).replace('.00', '');
    }

    sameId(left, right) {
      return this.toId(left) !== null && this.toId(left) === this.toId(right);
    }

    toId(value) {
      if (value === null || value === undefined || value === '') return null;
      var match = String(value).match(/\d+/g);
      if (!match || !match.length) return null;
      var parsed = parseInt(match[match.length - 1], 10);
      return Number.isFinite(parsed) ? parsed : null;
    }

    toNumber(value) {
      var parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    getDiscountPercent() {
      return this.toNumber(this.dataset.discountPercent) || 5;
    }

    getRestockPriceCents(tierKey) {
      var tier = this.tiers[tierKey] || this.tiers[300];
      var priceCents = tier ? tier.priceCents : this.defaultPriceCents(tierKey);
      return Math.round(priceCents * (100 - this.getDiscountPercent()) / 100);
    }

    getRestockPriceDollars(tierKey) {
      return this.getRestockPriceCents(tierKey) / 100;
    }

    getBonusValueDollars(tierKey) {
      var copy = COPY[tierKey] || COPY[300];
      return (copy.bonusValueCents || 0) / 100;
    }

    getButtonText(button) {
      return button ? button.textContent.trim().replace(/\s+/g, ' ') : '';
    }

    buildTrackingPayload(tierKey, params) {
      var copy = COPY[tierKey] || COPY[300];
      var tier = this.tiers[tierKey] || this.tiers[300] || {};

      return Object.assign({
        tier: tierKey || 'unknown',
        frequency: tier.frequency || null,
        bonus_value: (copy.bonusValueCents || 0) / 100,
        restock_price: this.getRestockPriceDollars(tierKey),
        discount_percent: this.getDiscountPercent(),
        has_matching_cart: this.hasMatchingCart
      }, params || {});
    }

    track(eventName, payload) {
      payload = Object.assign({
        offer_id: OFFER_ID,
        event_category: '556_auto_restock',
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title
      }, payload || {});

      Object.keys(payload).forEach(function(key) {
        if (payload[key] === undefined) delete payload[key];
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
      if (typeof window.gtag === 'function') window.gtag('event', eventName, payload);
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
    }

    safeTrack(eventName, payload) {
      try {
        this.track(eventName, payload);
      } catch (error) {
        // Tracking should never block the checkout path.
      }
    }

    redirectTo(url) {
      var target = url || '/checkout';
      try {
        window.location.assign(target);
      } catch (error) {
        window.location.href = target;
      }
    }
  });
})();
